import { LayoutMoon, TopNav, TopNavItem } from '@orobix/orobix-fe-components'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, useHistory, useLocation } from 'react-router-dom'

import { retrieveLoggedUser, useAuth, User } from '../auth/AuthProvider'
import { AppInfo } from '../components/AppInfo/AppInfo'
import { AppLogo } from '../components/AppLogo/AppLogo'
import { EnvInfo } from '../components/EnvInfo/EnvInfo'
import { vars } from '../utils/appConfig'
import { getEnvs } from '../utils/env'
import { locations } from '../utils/locations'

interface LoggedInTemplateProps {
  children: React.ReactNode
}

const { showEnvBadge } = getEnvs()

export const LoggedInTemplate = ({
  children,
}: LoggedInTemplateProps): JSX.Element => {
  const { t } = useTranslation()
  const history = useHistory()
  const { logout } = useAuth()
  const location = useLocation()
  const { bypassLogin } = getEnvs()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    retrieveLoggedUser().then((loggedUser: User | null) => {
      setUser(loggedUser)
      setIsLoading(false)
    })
  }, [])

  const handleMenuClick = useCallback(
    (where: string) => history.push(where),
    [history]
  )

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!user && !bypassLogin) {
    return <Redirect to="/login" />
  }

  let userFullName = 'Guest'
  let userInitials = 'G'
  let userEmail = ''
  if (user) {
    userEmail = user.email
    userFullName = `${user.firstName} ${user.lastName}`
    userInitials = user.firstName ? user.firstName.charAt(0).toUpperCase() : ''
  }

  return (
    <LayoutMoon
      appName={vars.appName}
      logo={<AppLogo />}
      subTitle="by Orobix"
      info={<AppInfo />}
      envBadge={showEnvBadge && <EnvInfo />}
      showUser
      userFullName={userFullName}
      userEmail={userEmail}
      userInitials={userInitials}
      userMenuItems={
        !bypassLogin
          ? [
              {
                type: 'button',
                label: t('Settings'),
                onClick: () => handleMenuClick(locations.settings),
              },
              {
                type: 'button',
                label: t('Logout'),
                onClick: () => logout(),
              },
            ]
          : [
              {
                type: 'button',
                label: t('Settings'),
                onClick: () => handleMenuClick(locations.settings),
              },
            ]
      }
      header={
        <TopNav>
          <TopNavItem
            active={location.pathname.startsWith(
              locations.optimizationProcesses
            )}
            label={t('Optimization processes')}
            onClick={() => handleMenuClick(locations.optimizationProcesses)}
          />
          <TopNavItem
            active={location.pathname.startsWith(locations.drugRegistry)}
            label={t('Drug registry')}
            onClick={() => handleMenuClick(locations.drugRegistry)}
          />
        </TopNav>
      }
      content={children}
    />
  )
}
